import { Injectable } from '@angular/core';
import {Observable, Subscriber} from 'rxjs';
import {ConfigurationModel} from '../shared/models/configuration.model';

export interface ISettingKeyValuePair {
  readonly key: string;
  readonly value: any;
}

export enum BaseMapType {
  OUTDOORS = 'Outdoors',
  DARK = 'Dark',
  IMAGERY = 'Imagery',
  WINTER = 'Winter',
  CUSTOM = 'Custom',
}

export enum StatusLayerType {
  COVERAGE = 'Coverage',
  CURRENCY = 'Currency',
  GPS_TRACKS = 'GPS Tracks',
  ASSIGNMENT_STATUS = 'Assignment Status',
  ROUTE_STATUS = 'Route Status',
  NONE = 'None'
}

export enum WeatherLayerType {
  RADAR = 'radar',
  WARNINGS = 'warnings',
  TRAFFIC = 'traffic',
}

export class ActivityFilter {
  static readonly NONE = 'none';
  static readonly PLOWING = 'plowing';
  static readonly GRANULAR_SPREADING = 'gspreading';
  static readonly LIQUID_SPREADING = 'lspreading';
  static readonly SPREADING = 'spreading';
  static readonly MOWING = 'mowing';
  static readonly SWEEPING = 'sweeping';
  static readonly PLOWING_MOWING_SWEEPING = 'plow_mow_sweep';

  static toBitNumberArray(activityFilter: ActivityFilter) {
    let bitNumbers: number[];
    switch (activityFilter) {
      case ActivityFilter.PLOWING:
        bitNumbers = [0, 1, 2, 3, 4];
        break;
      case ActivityFilter.GRANULAR_SPREADING:
        bitNumbers = [5, 6, 7, 8, 9];
        break;
      case ActivityFilter.LIQUID_SPREADING:
        bitNumbers = [10, 11, 12, 13, 14];
        break;
      case ActivityFilter.SPREADING:
        bitNumbers = [5, 6, 7, 8, 9, 10, 11, 12, 13, 14];
        break;
      case ActivityFilter.MOWING:
        bitNumbers = [15, 16, 17, 18, 19];
        break;
      case ActivityFilter.SWEEPING:
        bitNumbers = [20, 21, 22, 23, 24];
        break;
      case ActivityFilter.PLOWING_MOWING_SWEEPING:
        bitNumbers = [0, 1, 2, 3, 4, 15, 16, 17, 18, 19, 20, 21, 22, 23, 24];
        break;
      case ActivityFilter.NONE:
      default:
        bitNumbers = null;
    }
    return bitNumbers;
  }

  // example for PLOWING: sum(2^0 + 2^1 + 2^2 + 2^3 + 2^4) = 31
  static toFlagMask(activityFilter: ActivityFilter) {
    const bitNumbers = ActivityFilter.toBitNumberArray(activityFilter);
    return bitNumbers?.reduce((acc, bitNumber) => acc + 2 ** bitNumber, 0) || null;
  }

  // check if flags value fulfills the activity filter
  // return true if the activity filter should include this feature
  static flagCheck(activityFilter: ActivityFilter, flags: number) {
    return ActivityFilter.toBitNumberArray(activityFilter).reduce((acc, bitNumber) => {
      // tslint:disable-next-line:no-bitwise
      return acc || (flags & (2 ** bitNumber)) !== 0;
    }, false);
  }
}

@Injectable({
  providedIn: 'root'
})
export class SettingsService {
  static readonly TRACKS_LAYER_TYPE_KEY = 'Tracks';
  static readonly WEATHER_RADAR_LAYER_KEY = 'Radar';
  static readonly WEATHER_WARNINGS_LAYER_KEY = 'Warnings';
  static readonly TRAFFIC_LAYER_KEY = 'Traffic';

  static readonly PLOW_TRACK_TIME_FILTER = 'Plow Track: Time Filter';
  static readonly ACTIVITY_FILTER = 'Plow Track: Layer Filter';

  static readonly SHIFT_TRACK_TIME_FILTER = 'Shift Track: Time Filter';
  static readonly SHIFT_TRACK_ANIM_SPEED = 'Shift Track: Animation Speed';
  static readonly PLOW_TRACK_SHOW_RECENT = 'Plow Track: Show Recent';

  static readonly ROUTE_STATUS_PATH = 'Route Status Path';
  static readonly ASSIGNMENT_STATUS_THRESHOLD = 'Route Status Threshold';
  static readonly WEATHER_LOCATION = 'Weather Location';

  static readonly COVERAGE_TRACK_STYLE_1 = 'CoverageLevel1TrackStyle';
  static readonly COVERAGE_TRACK_STYLE_2 = 'CoverageLevel2TrackStyle';
  static readonly COVERAGE_TRACK_STYLE_3 = 'CoverageLevel3TrackStyle';
  static readonly COVERAGE_TRACK_STYLE_4 = 'CoverageLevel4TrackStyle';
  static readonly COVERAGE_TRACK_STYLE_5 = 'CoverageLevel5TrackStyle';

  static readonly CURRENCY_TRACK_STYLE_1 = 'CurrencyLevel1TrackStyle';
  static readonly CURRENCY_TRACK_STYLE_2 = 'CurrencyLevel2TrackStyle';
  static readonly CURRENCY_TRACK_STYLE_3 = 'CurrencyLevel3TrackStyle';
  static readonly CURRENCY_TRACK_STYLE_4 = 'CurrencyLevel4TrackStyle';
  static readonly CURRENCY_TRACK_STYLE_5 = 'CurrencyLevel5TrackStyle';

  static readonly GPS_TRACK_ACTIVE_TRACK_STYLE = 'GpsTrackActiveTrackStyle';
  static readonly GPS_TRACK_RECENT_TRACK_STYLE = 'GpsTrackRecentTrackStyle';

  static readonly ASSIGNMENT_STATUS_SERVICED_STYLE = 'RouteStatusServicedStyle';
  static readonly ASSIGNMENT_STATUS_IN_PROGRESS_STYLE = 'RouteStatusInProgressStyle';
  static readonly ASSIGNMENT_STATUS_NOT_SERVICED_STYLE = 'RouteStatusNotServicedStyle';

  static readonly ROUTE_STATUS_STYLE_NOT_DONE = 'RouteStatusDoneStyle';
  static readonly ROUTE_STATUS_STYLE_1 = 'RouteStatusLevel1Style';
  static readonly ROUTE_STATUS_STYLE_2 = 'RouteStatusLevel2Style';
  static readonly ROUTE_STATUS_STYLE_3 = 'RouteStatusLevel3Style';

  static readonly WHATS_NEW_SEEN_VERSION = 'Seen What\'s New Version';
  static readonly EVENTS_LAST_READ_TIME = 'Events Last Read Time';

  static readonly BASE_MAP_LAYER_KEY = 'BaseMap';
  static readonly CUSTOM_BASE_MAP_KEY = 'CustomBaseMap';

  static readonly VISIBLE_CUSTOM_MAP_LAYERS = 'CustomMapLayersVisibility';
  private static readonly localStorageKey = 'portal-settings';

  private booleanSettingsMap: Map<string, boolean>;
  private stringSettingsMap: Map<string, string>;

  private isInitialized = false;

  private readonly settingsChangedObservableSubscribers = new Array<Subscriber<ISettingKeyValuePair>>();
  readonly settingsChangedObservable = new Observable<ISettingKeyValuePair>((observer) => {
    if (this.booleanSettingsMap) {
      this.booleanSettingsMap.forEach((mapValue: boolean, mapKey: string) => {
        observer.next({ key: mapKey, value: mapValue});
      });
      this.stringSettingsMap.forEach((mapValue: string, mapKey: string) => {
        observer.next({ key: mapKey, value: mapValue});
      });
    }
    this.settingsChangedObservableSubscribers.push(observer);
  });

  constructor() { }

  getBooleanValue(key: string): boolean {
    if (!this.isInitialized) {
      console.log('Warn: Settings Service is not initialized yet!');
      return undefined;
    }

    return this.booleanSettingsMap.get(key);
  }

  setBooleanValue(key: string, value: boolean) {
    if (!this.isInitialized) {
      console.log('Warn: Settings Service is not initialized yet!');
      return;
    }

    const oldValue = this.getBooleanValue(key);
    if (value === oldValue) {
      return;
    }

    this.booleanSettingsMap.set(key, value);

    if (!!localStorage) {
      localStorage.setItem(SettingsService.localStorageKey + '_' + key, value ? 'true' : 'false');
    }

    this.onSettingsChanged(key, value);
  }

  getStringValue(key: string): string {
    if (!this.isInitialized) {
      console.log('Warn: Settings Service is not initialized yet!');
      return undefined;
    }

    return this.stringSettingsMap.get(key);
  }

  setStringValue(key: string, value: string) {
    if (!this.isInitialized) {
      console.log('Warn: Settings Service is not initialized yet!');
      return;
    }

    const oldValue = this.getStringValue(key);
    if (value === oldValue) {
      return;
    }

    this.stringSettingsMap.set(key, value);

    if (!!localStorage) {
      localStorage.setItem(SettingsService.localStorageKey + '_' + key, value);
    }

    this.onSettingsChanged(key, value);
  }

  public initSettings(tenantConfiguration: ConfigurationModel) {
    if (tenantConfiguration === null) {
      console.log('Initializing Settings Service failed! Empty tenant config!');
      return;
    }

    this.booleanSettingsMap = new Map<string, boolean>();
    this.stringSettingsMap = new Map<string, string>();

    // init tenant specific map layer settings
    const mapLayers = tenantConfiguration.additionalLayers;
    const mapLayerNames = mapLayers.map(mapLayer => mapLayer.name);
    for (const mapLayerName of mapLayerNames) {
      this.setDefaultBooleanValue(mapLayerName, false);
    }

    // recent tracks
    this.setDefaultBooleanValue(SettingsService.PLOW_TRACK_SHOW_RECENT, false);

    // init weather layers settings
    this.setDefaultBooleanValue(SettingsService.WEATHER_RADAR_LAYER_KEY, false);
    this.setDefaultBooleanValue(SettingsService.WEATHER_WARNINGS_LAYER_KEY, false);
    this.setDefaultBooleanValue(SettingsService.TRAFFIC_LAYER_KEY, false);

    // init base map layer settings
    this.setDefaultStringValue(SettingsService.BASE_MAP_LAYER_KEY, BaseMapType.WINTER);
    this.setDefaultStringValue(SettingsService.TRACKS_LAYER_TYPE_KEY, StatusLayerType.COVERAGE);
    this.setDefaultStringValue(SettingsService.CUSTOM_BASE_MAP_KEY, null);
    this.setDefaultStringValue(SettingsService.VISIBLE_CUSTOM_MAP_LAYERS, '');

    this.setDefaultStringValue(SettingsService.PLOW_TRACK_TIME_FILTER, String(tenantConfiguration.lookBackPeriod));
    this.setDefaultStringValue(SettingsService.ACTIVITY_FILTER, ActivityFilter.NONE);
    this.setDefaultStringValue(SettingsService.SHIFT_TRACK_TIME_FILTER, '0_100');
    this.setDefaultStringValue(SettingsService.SHIFT_TRACK_ANIM_SPEED, String(10));

    this.setDefaultStringValue(SettingsService.ROUTE_STATUS_PATH, '');
    this.setDefaultStringValue(SettingsService.ASSIGNMENT_STATUS_THRESHOLD, String(4));

    // load weather location
    this.setDefaultStringValue(SettingsService.WEATHER_LOCATION, null);

    this.setDefaultStringValue(SettingsService.COVERAGE_TRACK_STYLE_1, null);
    this.setDefaultStringValue(SettingsService.COVERAGE_TRACK_STYLE_2, null);
    this.setDefaultStringValue(SettingsService.COVERAGE_TRACK_STYLE_3, null);
    this.setDefaultStringValue(SettingsService.COVERAGE_TRACK_STYLE_4, null);
    this.setDefaultStringValue(SettingsService.COVERAGE_TRACK_STYLE_5, null);

    this.setDefaultStringValue(SettingsService.CURRENCY_TRACK_STYLE_1, null);
    this.setDefaultStringValue(SettingsService.CURRENCY_TRACK_STYLE_2, null);
    this.setDefaultStringValue(SettingsService.CURRENCY_TRACK_STYLE_3, null);
    this.setDefaultStringValue(SettingsService.CURRENCY_TRACK_STYLE_4, null);
    this.setDefaultStringValue(SettingsService.CURRENCY_TRACK_STYLE_5, null);

    this.setDefaultStringValue(SettingsService.GPS_TRACK_ACTIVE_TRACK_STYLE, null);
    this.setDefaultStringValue(SettingsService.GPS_TRACK_RECENT_TRACK_STYLE, null);

    this.setDefaultStringValue(SettingsService.ASSIGNMENT_STATUS_SERVICED_STYLE, null);
    this.setDefaultStringValue(SettingsService.ASSIGNMENT_STATUS_IN_PROGRESS_STYLE, null);
    this.setDefaultStringValue(SettingsService.ASSIGNMENT_STATUS_NOT_SERVICED_STYLE, null);

    this.setDefaultStringValue(SettingsService.ROUTE_STATUS_STYLE_NOT_DONE, null);
    this.setDefaultStringValue(SettingsService.ROUTE_STATUS_STYLE_1, null);
    this.setDefaultStringValue(SettingsService.ROUTE_STATUS_STYLE_2, null);
    this.setDefaultStringValue(SettingsService.ROUTE_STATUS_STYLE_3, null);

    // what's new seen version
    this.setDefaultStringValue(SettingsService.WHATS_NEW_SEEN_VERSION, '1.0');
    this.setDefaultStringValue(SettingsService.EVENTS_LAST_READ_TIME, null);

    this.isInitialized = true;
  }

  private setDefaultBooleanValue(propertyKey: string, defaultValue: boolean) {
    const savedValue = localStorage ? localStorage.getItem(SettingsService.localStorageKey + '_' + propertyKey) : null;
    if (savedValue !== null) {
      this.booleanSettingsMap.set(propertyKey, savedValue === 'true');
    } else {
      this.booleanSettingsMap.set(propertyKey, defaultValue);
    }
  }

  private setDefaultStringValue(propertyKey: string, defaultValue: string) {
    const savedStringValue = localStorage ? localStorage.getItem(SettingsService.localStorageKey + '_' + propertyKey) : null;
    if (savedStringValue !== null) {
      this.stringSettingsMap.set(propertyKey, savedStringValue);
    } else {
      this.stringSettingsMap.set(propertyKey, defaultValue);
    }
  }

  private onSettingsChanged(changedKey: string, newValue: any) {
    for (const listener of this.settingsChangedObservableSubscribers) {
      listener.next({ key: changedKey, value: newValue });
    }
  }
}
